import Layout from "../../../common/layouts/Layout";
import Navbar from "../../../common/Navbar/Navbar";
import {
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import styles from "./IRBDetails.module.scss";
import React, { FC, useCallback, useEffect, useState } from "react";
import {
  useGetIrbSubmissionInfoLazyQuery,
  useGetIrbSubmissionInfoQuery,
  useRemoveIrbMutation,
  useReUploadDocMutation,
} from "../../../../generated/graphql";
import UploadFile from "../../../common/UploadFile/UploadFile";
import { useNavigate, useParams } from "react-router-dom";
import Moment from "../../../common/Moment/Moment";
import {
  IRB_SUBMISSION_TYPES,
  ROLES,
  RoutingList,
  SUBMISSION_TYPES,
} from "../../../../constants";
import { romanize } from "../../../../utils/helper";
import ReadReviewDialog from "../../../common/ReadReviewDialog/ReadReviewDialog";
import UploadFileAgain from "../../../common/UploadFileAgain/UploadFileAgain";
import WithPermission from "../../../common/WithPermission/WithPermission";
import useAuthStore from "../../../../states/zustand/authStore";
import useAppStore from "../../../../states/zustand/appStore";
import { toast } from "react-toastify";

const IRBDetails: FC = (props): JSX.Element => {
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const { profileData } = useAuthStore((state) => state);

  const navigate = useNavigate();
  const { id } = useParams() as any;
  const [getIrb, { data, loading }] = useGetIrbSubmissionInfoLazyQuery();
  const [removeIrb] = useRemoveIrbMutation();
  const { setLoader } = useAppStore();

  useEffect(() => {
    setLoader(loading);
  }, [loading]);

  const fetchDetail = useCallback(() => {
    return getIrb({
      variables: { id: id },
    });
  }, [id]);
  useEffect(() => {
    fetchDetail();
  }, [id]);

  const [selectedDoc, setSelectedDoc] = useState<{
    docId: string;
    label: string;
  }>({
    docId: "",
    label: "",
  });

  const handleEdit = () => {
    navigate(RoutingList.irb.edit.path.replace(":id", id));
  };

  const openReview = (doc: { docId: string; label: string }) => {
    if (doc.docId) {
      setSelectedDoc(doc);
      setOpenReviewDialog(true);
    }
  };

  const handleDelete = async () => {
    try {
      let res = await removeIrb({
        variables: {
          _id: id,
        },
      });
      if (!res.data) throw null;
      navigate(RoutingList.irb.index.path);

      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Could not delete!");
    }
  };

  return (
    <Layout>
      <>
        {selectedDoc && (
          <ReadReviewDialog
            open={openReviewDialog}
            setOpen={setOpenReviewDialog}
            onClose={fetchDetail}
            docId={selectedDoc.docId}
            title={selectedDoc.label}
            submissionType={SUBMISSION_TYPES.IRB}
            submissionSubType={data?.irb?.type || ""}
          />
        )}
        <Navbar pageTitle="MIRB Submission" />
        <Container maxWidth={false}>
          <Card>
            <CardContent className={`pd-x-30 pd-y-30`}>
              <div
                className={` d-flex align-items-center justify-content-between`}
              >
                <Typography
                  variant={"body1"}
                  component={"p"}
                  className={`font-size-lg font-weight-bold`}
                >
                  {data?.irb?.title} - #{data?.irb?.protocolNumber}
                </Typography>
                <WithPermission roles={[ROLES.INVESTIGATOR]}>
                  <div>
                    <Button
                      onClick={handleEdit}
                      variant={"outlined"}
                      color={"error"}
                      className="mg-r-10 btn-sm"
                      type="button"
                    >
                      <i className={`icon-edit mg-r-10`}></i>
                      <span>Edit</span>
                    </Button>
                    <Button
                      onClick={handleDelete}
                      variant={"outlined"}
                      color={"error"}
                      className="btn-sm"
                      type="button"
                    >
                      <i className={`icon-trash mg-r-10`}></i>
                      <span>Delete</span>
                    </Button>
                  </div>
                </WithPermission>
              </div>
              <Divider className={`mg-t-30 mg-b-20`} />
              <section>
                <Typography
                  variant={"h2"}
                  component={"h2"}
                  className={`mg-b-20`}
                >
                  1. Study Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Protocol Number
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.protocolNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Study Title
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Sponsor
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.sponsor}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Principal Investigator
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.principalInvestigator}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Department{" "}
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.department}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Email{" "}
                      <Typography
                        variant={"body2"}
                        component={"span"}
                        color={"secondary"}
                      >
                        (EC, CRC and PI)
                      </Typography>
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.emails?.join(", ")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Study Phase
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.studyPhase}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Type of Study
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.typeOfStudy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Funding / Grant
                    </Typography>
                    <div className={`d-flex align-items-center`}>
                      <div className={`bg-light uploaded-info pd-x-20 mg-r-10`}>
                        {data?.irb?.isFunded ? "Yes" : "No"}
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Type of Funding
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.fundType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      MICR #
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.irb?.micr}
                    </Typography>
                  </Grid>
                </Grid>
              </section>
              <Divider className={`mg-y-30`} />
              <section>
                <Typography
                  variant={"h2"}
                  component={"h2"}
                  className={`mg-b-20`}
                >
                  2. MIRB Submission
                </Typography>
                <div className={`mb-b-20`}>
                  <Typography
                    variant={"h4"}
                    component={"h4"}
                    className={`mg-b-2`}
                  >
                    2.1 Date of MIRB Submission{" "}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    component={"p"}
                    className={`font-weight-semi-bold mg-b-20`}
                  >
                    <Moment date={data?.irb?.dateOfIRBSubmission} format="ll" />
                  </Typography>
                </div>
                <Typography
                  variant={"h4"}
                  component={"h4"}
                  className={`mg-b-20`}
                >
                  2.2 Document Submitted - MIRB{" "}
                </Typography>
                <ul className={`list-style-none mg-b-10 submissionList`}>
                  {data?.irb?.documents?.map((d, i) => {
                    return (
                      <li key={d._id}>
                        <Grid
                          container
                          spacing={2}
                          className={`align-items-center justify-content-between`}
                        >
                          <Grid item xs={12} md={"auto"}>
                            <label className="form-label mg-b-0 font-size-14">
                              {romanize(i + 1)}). {d.label} :
                            </label>
                          </Grid>
                          <Grid item xs={12} md={"auto"}>
                            <div
                              className={`d-flex justify-content-between align-items-center`}
                            >
                              <div>
                                <WithPermission roles={[ROLES.INVESTIGATOR]}>
                                  <UploadFileAgain
                                    submissionType={SUBMISSION_TYPES.IRB}
                                    name={d.label || ""}
                                    protocolNumber={data.irb.protocolNumber}
                                    docId={d._id}
                                    uploadBtnText={`Upload Again`}
                                  />
                                </WithPermission>
                              </div>
                              <Button
                                variant={"outlined"}
                                color={
                                  d.isReadAndAcknowledged
                                    ? "success"
                                    : "secondary"
                                }
                                size={"small"}
                                className={`pd-x-12 mg-l-10 font-weight-medium text-nowrap`}
                                onClick={() =>
                                  openReview({
                                    docId: d._id || "",
                                    label: d.label || "",
                                  })
                                }
                              >
                                <i className="icon-chat font-size-lg mg-r-10"></i>
                                {profileData.role === ROLES.INVESTIGATOR
                                  ? "Read"
                                  : "Read & Review"}
                              </Button>
                            </div>
                          </Grid>
                        </Grid>
                      </li>
                    );
                  })}
                </ul>

                <Typography
                  variant={"h4"}
                  component={"h4"}
                  className={`mg-b-20`}
                  sx={{ marginTop: "50px" }}
                >
                  2.2 IRB Presentation
                </Typography>
                <ul className={`list-style-none mg-b-10 submissionList`}>
                  <li>
                    <Grid
                      container
                      spacing={2}
                      className={`align-items-center justify-content-between`}
                    >
                      <Grid item xs={12} md={"auto"}>
                        <label className="form-label mg-b-0 font-size-14">
                          i). IRB Presentation file :
                        </label>
                      </Grid>
                      <Grid item xs={12} md={"auto"}>
                        <div
                          className={`d-flex justify-content-between align-items-center`}
                        >
                          <Button
                            variant={"outlined"}
                            color={"secondary"}
                            size={"small"}
                            className={`pd-x-12 mg-l-10 font-weight-medium text-nowrap`}
                            onClick={() => window.open(data?.irb?.IRBPresentation, "_blank")}
                          >
                            <i className="icon-download font-size-lg mg-r-10"></i>{" "}
                            Read
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </li>
                </ul>
              </section>
            </CardContent>
          </Card>
        </Container>
      </>
    </Layout>
  );
};

export default IRBDetails;
